import React, { useState, useMemo, useEffect } from "react";
import { cn } from "@/lib/utils";
import tinycolor from "tinycolor2";
import axios from "axios";
import { useIframeStore } from "@/store/zustand/iframeStore";
import { useTypedSelector } from "@/store/store";
import { SearchInput } from "@/components/ui/search-input";
import {
  normalizeColorValue,
  isValidCssColor,
  parseColorValue,
} from "./input/color-picker/utils/colorFormatUtils";
import ColorSwatch from "./input/ColorSwatch";
import {
  formatCssVariableName,
  recursivelyResolveCssVariable,
} from "@/utils/iframeUtils";
import { useCanvasId } from "@/utils/utils";
import { useSession } from "@clerk/clerk-react";
import {
  useColorStore,
  type ColorGroup,
  type TailwindColor,
  type BackendColorGroup,
  type BackendColorToken,
} from "@/store/zustand/colorStore";

interface ComboBoxOptionType {
  tailwind: string;
  css: string;
  displayValue?: string; // Optional display value for UI
}

interface LibraryColorPanelProps {
  onColorSelected: (option: ComboBoxOptionType) => void;
  className?: string;
  comboboxOptions?: Array<{ tailwind: string; css: string }>;
}

/**
 * Utility function to convert modern CSS space-separated HSL syntax to hex color
 * @param cssValue - CSS value that might contain space-separated HSL syntax
 * @returns hex color string if valid, null otherwise
 */
const parseSpaceSeparatedHsl = (cssValue: string): string | null => {
  if (
    cssValue.includes("%") &&
    !cssValue.startsWith("#") &&
    !cssValue.startsWith("hsv")
  ) {
    const hslParts = cssValue.split(/\s+/);
    if (hslParts.length >= 3) {
      const h = hslParts[0];
      const s = hslParts[1].endsWith("%") ? hslParts[1] : `${hslParts[1]}%`;
      const l = hslParts[2].endsWith("%") ? hslParts[2] : `${hslParts[2]}%`;
      const tinycolorHsl = `hsl(${h}, ${s}, ${l})`;
      const tc = tinycolor(tinycolorHsl);
      if (tc.isValid()) {
        return tc.toHexString();
      }
    }
  }
  return null;
};

const LibraryColorPanel: React.FC<LibraryColorPanelProps> = ({
  onColorSelected,
  className,
  comboboxOptions = [],
}) => {
  const { session } = useSession();

  const [searchQuery, setSearchQuery] = useState("");
  const sendMessageToIFrame = useIframeStore(
    (store) => store.sendMessageToIFrame
  );
  const cssCustomProperties = useTypedSelector(
    (state) => state.iframe.cssCustomProperties
  );
  const canvasId = useCanvasId();

  // Use color store
  const { getColors, setColors, isLoading, setLoading } = useColorStore();

  const colorGroups = getColors(canvasId || "") || [];
  const loading = isLoading();

  // Extract CSS custom properties from iframe
  // biome-ignore lint/correctness/useExhaustiveDependencies: only run once
  useEffect(() => {
    const extractCssCustomProperties = () => {
      sendMessageToIFrame({
        type: "executeFunction",
        payload: {
          functionName: "extractCssCustomProperties",
          args: [],
        },
      });
    };

    // Only extract CSS properties if they're not already in the store
    if (!cssCustomProperties) {
      extractCssCustomProperties();
    }
  }, []);

  // Fetch colors from backend and combine with CSS custom properties
  useEffect(() => {
    if (!canvasId || !session || loading) {
      return;
    }

    // Only fetch if we don't have cached colors
    if (colorGroups.length > 0) {
      return;
    }

    const fetchColors = async () => {
      const supabaseJWT = await session.getToken();
      if (!supabaseJWT) {
        return;
      }
      try {
        setLoading(true);

        // Fetch colors from backend API
        const response = await axios.get(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/canvases/${canvasId}/tailwind/colors`,
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
              "Content-Type": "application/json",
            },
          }
        );

        const backendColorGroups: BackendColorGroup[] = response.data;

        if (!Array.isArray(backendColorGroups)) {
          console.warn(
            "Backend colors response is not an array:",
            backendColorGroups
          );
          setColors(canvasId, []);
          setLoading(false);
          return;
        }

        // Convert backend format to frontend format
        const convertedColorGroups: ColorGroup[] = backendColorGroups.map(
          (group) => ({
            name: group.name,
            colors: group.colors.map((color) => {
              // For colors with "DEFAULT" in the name, show "DEFAULT" as the display name
              const displayName = color.name.includes(" DEFAULT")
                ? "DEFAULT"
                : color.name;

              // Try to resolve the color value using CSS custom properties if available
              let resolvedValue = color.color;
              let hexValue = color.color;

              if (cssCustomProperties && color.color.includes("var(--")) {
                const varMatch = color.color.match(/var\(--([^)]+)\)/);
                if (varMatch) {
                  const varName = `--${varMatch[1]}`;
                  const cssValue = cssCustomProperties[varName];
                  if (cssValue && isValidCssColor(cssValue)) {
                    resolvedValue = cssValue;
                    const tc = tinycolor(cssValue);
                    if (tc.isValid()) {
                      hexValue = tc.toHexString();
                    } else {
                      // Handle modern CSS space-separated HSL syntax
                      const parsedHex = parseSpaceSeparatedHsl(cssValue);
                      if (parsedHex) {
                        hexValue = parsedHex;
                      }
                    }
                  }
                }
              }

              return {
                name: displayName,
                path: color.id.replace(/_/g, "-"), // Convert backend ID format to path format
                rawValue: color.color,
                resolvedValue,
                hexValue,
                group: group.name,
                displayValue: hexValue,
              };
            }),
          })
        );

        // Sort colors within each group (DEFAULT first)
        for (const group of convertedColorGroups) {
          group.colors.sort((a, b) => {
            if (a.name === "DEFAULT") return -1;
            if (b.name === "DEFAULT") return 1;
            return a.name.localeCompare(b.name);
          });
        }

        // Sort groups alphabetically
        convertedColorGroups.sort((a, b) => a.name.localeCompare(b.name));

        setColors(canvasId, convertedColorGroups);
      } catch (error) {
        console.error("Error fetching colors from backend:", error);
        setColors(canvasId, []);
      } finally {
        setLoading(false);
      }
    };

    fetchColors();
  }, [
    canvasId,
    session,
    loading,
    colorGroups.length,
    setColors,
    setLoading,
    cssCustomProperties,
  ]); // Re-run when canvasId changes or if no colors cached

  const filteredColorGroups = useMemo(() => {
    if (!searchQuery.trim()) {
      return colorGroups;
    }

    const query = searchQuery.toLowerCase();
    return colorGroups
      .map((group) => ({
        ...group,
        colors: group.colors.filter(
          (color) =>
            color.name.toLowerCase().includes(query) ||
            color.rawValue.toLowerCase().includes(query) ||
            color.path.toLowerCase().includes(query)
        ),
      }))
      .filter((group) => group.colors.length > 0);
  }, [colorGroups, searchQuery]);

  if (loading) {
    return (
      <div className={cn("p-4 text-center", className)}>
        <div className="text-secondary-n-500 text-sm">
          Loading colors from your Tailwind configuration...
        </div>
      </div>
    );
  }

  if (colorGroups.length === 0) {
    return (
      <div className={cn("p-4 text-center", className)}>
        <div className="text-secondary-n-500 text-sm">
          No custom colors found in your Tailwind configuration.
        </div>
        <div className="text-secondary-n-600 text-2xs mt-1">
          Add custom colors to your tailwind.config.js to see them here.
        </div>
      </div>
    );
  }

  return (
    <div className={cn("", className)}>
      {/* Search Bar */}
      <div className="border-y border-secondary-n-900 py-2">
        <SearchInput
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onClear={() => setSearchQuery("")}
        />
      </div>

      {/* Color Groups */}
      <div className="max-h-80 overflow-y-auto hide-scrollbar">
        {filteredColorGroups.length === 0 ? (
          <div className="p-4 text-center">
            <div className="text-secondary-n-500 text-sm">
              No colors match your search.
            </div>
          </div>
        ) : (
          <div className="py-2">
            {filteredColorGroups.map((group) => (
              <ColorGroupComponent
                key={group.name}
                group={group}
                onColorSelected={onColorSelected}
                comboboxOptions={comboboxOptions}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface ColorItemProps {
  color: TailwindColor;
  onSelected: (option: ComboBoxOptionType) => void;
  comboboxOptions: Array<{ tailwind: string; css: string }>; // Add this prop
}

const ColorItem: React.FC<ColorItemProps> = ({
  color,
  onSelected,
  comboboxOptions,
}) => {
  const cssCustomProperties = useTypedSelector(
    (state) => state.iframe.cssCustomProperties
  );

  const displayColor = useMemo(() => {
    // Try to resolve CSS custom properties from iframe
    if (cssCustomProperties && color.rawValue.includes("var(--")) {
      const varMatch = color.rawValue.match(/var\(--([^)]+)\)/);
      if (varMatch) {
        const varName = `--${varMatch[1]}`;
        const resolvedValue = cssCustomProperties[varName];
        if (resolvedValue && isValidCssColor(resolvedValue)) {
          const tc = tinycolor(resolvedValue);
          if (tc.isValid()) {
            return tc.toHexString();
          }

          // Handle modern CSS space-separated HSL syntax if tinycolor fails
          const parsedHex = parseSpaceSeparatedHsl(resolvedValue);
          if (parsedHex) {
            return parsedHex;
          }
        }
      }
    }

    // Try to parse the semantic Tailwind class using comboboxOptions
    const tailwindClass = `bg-${color.path}`;
    const parsed = parseColorValue(tailwindClass, comboboxOptions);
    if (parsed.isValid) {
      return parsed.hex;
    }

    // Fallback to backend values or frontend resolution
    const value = color.displayValue || color.resolvedValue || color.rawValue;

    // Try to normalize the color value to get a hex code
    const normalizedColor = normalizeColorValue(value);

    // If normalizeColorValue returns a valid hex code, use it
    if (normalizedColor?.startsWith("#")) {
      return normalizedColor;
    }

    // If the value is still a CSS variable that couldn't be resolved, show formatted name
    if (value.includes("var(--")) {
      return formatCssVariableName(value);
    }

    // Final fallback
    return normalizedColor || value;
  }, [color, cssCustomProperties, comboboxOptions]);

  const handleClick = () => {
    // For DEFAULT colors, use the shorter form (bg-primary instead of bg-primary-DEFAULT)
    // since Tailwind treats them as equivalent
    const colorPath = color.path.endsWith("-DEFAULT")
      ? color.path.replace("-DEFAULT", "")
      : color.path;

    const tailwindClass = `bg-${colorPath}`;

    // Find the matching option in comboboxOptions to get the proper CSS value
    const matchingOption = comboboxOptions.find(
      (option) => option.tailwind === tailwindClass
    );

    // For display, show the group name for DEFAULT colors, otherwise show the path
    const displayValue =
      color.name === "DEFAULT" ? color.group.toLowerCase() : colorPath;

    const option: ComboBoxOptionType = {
      tailwind: tailwindClass,
      css: matchingOption?.css || `background-color: ${color.rawValue}`,
    };
    onSelected({
      ...option,
      displayValue,
    });
  };

  return (
    <button
      type="button"
      className="w-full flex items-center justify-between p-2 py-1 hover:bg-background-tertiary transition-colors duration-150 text-left cursor-pointer"
      onClick={handleClick}
    >
      <div className="flex items-center gap-2 min-w-0">
        <ColorSwatch color={displayColor} size="sm" />
        <span className="text-xs text-secondary-n-100 font-medium pr-2 truncate capitalize">
          {color.name}
        </span>
      </div>
      <span className="text-xs text-secondary-n-500 font-mono uppercase pl-2 flex-shrink-0">
        {displayColor}
      </span>
    </button>
  );
};

interface ColorGroupComponentProps {
  group: ColorGroup;
  onColorSelected: (option: ComboBoxOptionType) => void;
  comboboxOptions: Array<{ tailwind: string; css: string }>;
}

const ColorGroupComponent: React.FC<ColorGroupComponentProps> = ({
  group,
  onColorSelected,
  comboboxOptions,
}) => {
  if (group.colors.length === 0) return null;

  return (
    <div className="mb-2">
      <h3 className="text-xs font-medium text-text-secondary my-1 px-2">
        {group.name}
      </h3>
      <div className="">
        {group.colors.map((color) => (
          <ColorItem
            key={color.path}
            color={color}
            onSelected={onColorSelected}
            comboboxOptions={comboboxOptions}
          />
        ))}
      </div>
    </div>
  );
};

export default React.memo(LibraryColorPanel);
